import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/lib/api';
import { type AttachedFile, documents, refusal } from './documents';

/** A file on its way: shown with its progress until the server lists it. */
export interface Upload {
  id: string;
  requirementId: string;
  fileName: string;
  progress: number;
}

export interface DocumentProblem {
  requirementId: string;
  fileName: string;
  message: string;
}

/** How often to ask whether pending scans have finished. */
export const POLL_MS = 3000;

/**
 * The application's documents: what is attached, what is uploading, and what was
 * refused, per requirement. While any file waits for its scan, the list is re-read
 * every few seconds so its state changes without a reload.
 */
export function useDocuments(applicationId: string) {
  const [files, setFiles] = useState<AttachedFile[] | null>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [problems, setProblems] = useState<DocumentProblem[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const counter = useRef(0);

  const refresh = useCallback(async () => {
    try {
      setFiles(await documents.list(applicationId));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, [applicationId]);

  useEffect(() => {
    const first = setTimeout(() => void refresh(), 0);
    return () => clearTimeout(first);
  }, [refresh]);

  const pending = files?.some((f) => f.scanStatus === 'PENDING') ?? false;
  useEffect(() => {
    if (!pending) return undefined;
    const timer = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(timer);
  }, [pending, refresh]);

  const attach = useCallback(
    async (requirementId: string, chosen: readonly File[]) => {
      setProblems((before) => before.filter((p) => p.requirementId !== requirementId));
      await Promise.all(
        chosen.map(async (file) => {
          const refused = refusal(file);
          if (refused !== null) {
            setProblems((before) => [...before, { requirementId, fileName: file.name, message: refused }]);
            return;
          }
          counter.current += 1;
          const id = `upload-${counter.current}`;
          setUploads((before) => [...before, { id, requirementId, fileName: file.name, progress: 0 }]);
          try {
            await documents.attach(applicationId, requirementId, file, (progress) =>
              setUploads((before) => before.map((u) => (u.id === id ? { ...u, progress } : u))),
            );
          } catch (error) {
            const message =
              error instanceof ApiError || error instanceof Error ? error.message : 'The file could not be attached.';
            setProblems((before) => [...before, { requirementId, fileName: file.name, message }]);
          } finally {
            setUploads((before) => before.filter((u) => u.id !== id));
          }
        }),
      );
      await refresh();
    },
    [applicationId, refresh],
  );

  const remove = useCallback(
    async (fileId: string) => {
      try {
        await documents.remove(applicationId, fileId);
      } catch (error) {
        const file = files?.find((f) => f.fileId === fileId);
        setProblems((before) => [
          ...before,
          {
            requirementId: file?.requirementId ?? '',
            fileName: file?.fileName ?? '',
            message: error instanceof Error ? error.message : 'The file could not be removed.',
          },
        ]);
      }
      await refresh();
    },
    [applicationId, files, refresh],
  );

  return { files, uploads, problems, loadFailed, attach, remove };
}

export type Documents = ReturnType<typeof useDocuments>;
